import { useEffect, useMemo, useRef, useState } from "react";
import { Form, Upload, message } from "antd";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  CraRiskResult,
  FileType,
  RiskAssessmentAuditStatus,
  deleteAttachment,
  getCraAttachmentConfig,
  getCraResultByAnswer,
  getOrderAttachments,
  getOrderDetail,
  getProductCompanyList,
  getRiskEvaluation,
  getSignatureConfig,
  getUserInfo,
  saveCraInsuredPolicy,
  saveCustomerRiskAssessment,
  uploadAttachment,
  uploadFile,
  useMetadataStore,
} from "@ifa/utils";
import type {
  CraResultVO,
  OrderAttachmentConfig,
  OrderAttachmentDTO,
  OrderCra,
  OrderCraDTO,
  OrderDetail,
  ProductCompanyListVO,
  UserInfo,
  HighPremiumOrder,
} from "@ifa/utils";
import type { UploadFile } from "antd/es/upload/interface";
import pdfIcon from "../../assets/img/pdf.png";
import excelIcon from "../../assets/img/excel.png";
import wordIcon from "../../assets/img/word.png";
import { useImageCompression } from "@ifa/ui";
import { useIOSConfirm } from "../../hooks/useIOSConfirm";
import type { RiskAssessmentVO } from "./types/risk";
import type { SignaturePadRef } from "./SignaturePad";
import { isZuuCraVariant, resolveCraPresentationVariant } from "./craVariant";
import { CRA_PROJECT_LABELS } from "./craProjectLabels";
import { buildRiskAssessmentAnswers } from "./riskAssessmentShared";
import { ZUU_CRA_FIELDS, getZuuCraFieldValue } from "./zuuCraFieldMap";

export function useRiskAssessmentController({ hideHeader = false, readOnly = false }: { hideHeader?: boolean; readOnly?: boolean }) {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const fromOwnAccount = Form.useWatch("fromOwnAccount", form);
  const referredByThirdParty = Form.useWatch("referredByThirdParty", form);
  const instructionThroughThirdParty = Form.useWatch("instructionThroughThirdParty", form);
  const { confirm } = useIOSConfirm();

  // 使用图片压缩 Hook
  const { handleBeforeUpload } = useImageCompression({
    confirm,
  });

  const [loading, setLoading] = useState(false);
  const [originalCraData, setOriginalCraData] = useState<OrderCra | null>(null);
  const [orderData, setOrderData] = useState<OrderDetail | null>(null);

  // ORDER_AUDIT / COMPLIANCE_AUDIT / COMPLETED 状态下强制只读
  // 只在待审核（PENDING_REVIEW）和审核通过（APPROVED）时只读
  const isReadOnly =
    readOnly ||
    orderData?.riskAssessmentAuditStatus === RiskAssessmentAuditStatus.Submitted ||
    orderData?.riskAssessmentAuditStatus === RiskAssessmentAuditStatus.Approved ||
    orderData?.riskAssessmentAuditStatus === RiskAssessmentAuditStatus.Initial;
  const [highPremiumOrders, setHighPremiumOrders] = useState<(HighPremiumOrder & { readOnly?: boolean })[]>([]);
  const [companyList, setCompanyList] = useState<ProductCompanyListVO[]>([]);
  const [craAttachmentConfig, setCraAttachmentConfig] = useState<OrderAttachmentConfig[]>([]);
  const [signatureImage, setSignatureImage] = useState<string>(""); // 业务代表签名

  // High Premium Order Modal State
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<(HighPremiumOrder & { key?: React.Key }) | undefined>(undefined);

  // Risk Result Modal State
  const [isRiskModalOpen, setIsRiskModalOpen] = useState(false);
  const [craResults, setCraResults] = useState<CraResultVO[]>([]);
  const [loadingRiskResults, setLoadingRiskResults] = useState(false);

  // Signature Modal State
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [tempSignature, setTempSignature] = useState<string>(""); // 临时签名，用于弹窗中
  const [isSignatureReady, setIsSignatureReady] = useState(false);
  const signaturePadRef = useRef<SignaturePadRef>(null);
  const [currentUser, setCurrentUser] = useState<UserInfo | null>(null);

  const craVersion = useMetadataStore((state) => state.craVersion);
  const craVariant = resolveCraPresentationVariant({ craVersion });
  const isZuuMode = isZuuCraVariant({ craVersion });

  // 获取当前登录用户信息（业务代表）
  useEffect(() => {
    const uid = localStorage.getItem("uid");
    if (uid) {
      getUserInfo({ uid: Number(uid) }).then((res) => {
        setCurrentUser(res);
      });
    }
  }, []);

  // Fetch data from API
  useEffect(() => {
    // Fetch Company List
    getProductCompanyList().then((res) => {
      setCompanyList(res || []);
    });

    if (orderId) {
      setLoading(true);

      // Fetch Order Detail for header info
      getOrderDetail(Number(orderId)).then((res) => {
        if (res && res) {
          setOrderData(res);
        }
      });

      getRiskEvaluation(Number(orderId))
        .then((res) => {
          console.log(res);

          if (res) {
            const craData = res;
            setOriginalCraData(craData);
            setHighPremiumOrders(craData.insuredPolicy.highPremiumOrders || []);
            const initialValues: any = {};
            const checkboxFields = new Set([
              "initialPremiumPaymentMethod",
            ]);

            const directFields = [
              "pep",
              "fullUboAndDirectorList",
              "customerMediumRiskSituation",
              "customerHighRiskSituation",
              "validIdentityDocuments",
              "cooperative",
              "sourceOfFundsConsistent",
              "notHighRiskBusiness",
              "paidInitialPremium",
              "fromOwnAccount",
              "thirdPartyPayerRelation",
              "thirdPartyPayerInfo",
              "initialPremiumPaymentMethod",
              "referredByThirdParty",
              "instructionThroughThirdParty",
              "thirdPartyInstructionProof",
              "faceToFaceCommunication",
              "needsNonFaceToFaceSafeguards",
              "residentInHK",
              "nonHKResidenceCategory",
              "providesAnonymity",
              "involvesLargeCash",
              "frequentPurchasesWithoutReason",
              "supportsHighFrequencyIrregularActivity",
              "allowsLargeWithdrawals",
              "exceedsHighPremiumThreshold",
            ];

            if (craData.answer && Array.isArray(craData.answer)) {
              craData.answer.forEach((item: any) => {
                const key = item.question;
                if (key && directFields.includes(key)) {
                  if (item.type === "INPUT") {
                    if (item.value !== undefined) {
                      initialValues[key] = item.value;
                    } else if (item.items && item.items.length > 0) {
                      initialValues[key] = item.items[0].value;
                    }
                  } else if (item.type === "CHECKBOX") {
                    if (item.items && item.items.length > 0) {
                      const selectedItems = item.items.filter((opt: any) => opt.value === true);
                      initialValues[key] = selectedItems.map((opt: any) => opt.key);
                    }
                  } else if (item.type === "RADIO") {
                    if (item.items && item.items.length > 0) {
                      const selectedItem = item.items.find((opt: any) => opt.value === true);
                      if (selectedItem) {
                        initialValues[key] = selectedItem.key;
                      }
                    }
                  } else if (item.items && item.items.length > 0) {
                    const selectedItems = item.items.filter((opt: any) => opt.value === true);
                    if (selectedItems.length > 0) {
                      initialValues[key] = checkboxFields.has(key)
                        ? selectedItems.map((opt: any) => opt.key)
                        : selectedItems[0].key;
                    }
                  } else if (item.value !== undefined) {
                    initialValues[key] = item.value;
                  }
                }
              });
            }

            directFields.forEach((field) => {
              if (initialValues[field] === undefined && craData[field] !== undefined && craData[field] !== null) {
                if (checkboxFields.has(field) && Array.isArray(craData[field])) {
                  initialValues[field] = craData[field];
                } else {
                  initialValues[field] = craData[field];
                }
              }
            });

            if (isZuuMode) {
              ZUU_CRA_FIELDS.forEach((field) => {
                const value = getZuuCraFieldValue(craData, field);
                if (value !== undefined) {
                  initialValues[field] = value;
                }
              });
            }

            // 3. Set default value for fullUboAndDirectorList to "C"
            if (initialValues.fullUboAndDirectorList === undefined) {
              initialValues.fullUboAndDirectorList = "C";
            }

            // 4. 恢复业务代表签名
            if (craData.signatureImage) {
              setSignatureImage(craData.signatureImage);
            }

            console.log("Mapped Risk Assessment Data:", initialValues);
            form.setFieldsValue(initialValues);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch risk evaluation:", err);
          message.error("獲取風險評估數據失敗");
        })
        .finally(() => {
          setLoading(false);
        });

      // Fetch existing CRA attachments
      const fetchCraAttachments = async () => {
        const craTypes = [
          FileType.CraBrokerReport,
          FileType.CraFinancialQuestionnaire,
          FileType.RiskEvaluationFormAttachment,
          FileType.CraAssetIncomeProof,
          FileType.CraPremiumPaymentProof,
          FileType.CraThiredPartyRelaTionShipCertificate,
        ];

        try {
          const results = await Promise.all(craTypes.map((type) => getOrderAttachments(Number(orderId), type as any).catch(() => [])));

          const newFiles: Record<string, UploadFile[]> = {};

          results.forEach((files, index) => {
            const type = craTypes[index];
            if (files && files.length > 0) {
              newFiles[type] = files.map((f: any) => ({
                uid: f.id ? String(f.id) : f.attachmentId ? String(f.attachmentId) : String(Math.random()),
                name: f.name || f.fileName || "Unknown File",
                status: "done",
                url: f.url || f.path || f.fileUrl,
                path: f.path,
                ossPath: f.ossPath,
                attachmentId: f.attachmentId,
              }));
            }
          });

          if (Object.keys(newFiles).length > 0) {
            setUploadFiles((prev) => ({ ...prev, ...newFiles }));
          }
        } catch (error) {
          console.error("Failed to fetch CRA attachments:", error);
        }
      };
      fetchCraAttachments();

      // Fetch Signature Config for uploaded files
      getSignatureConfig(Number(orderId)).then((res) => {
        if (res && res.configs) {
          const loadedFiles: Record<string, { name: string; url: string }[]> = {};
          res.configs.forEach((item) => {
            // Only add if type and fileUrl are present
            if (item.type && item._fileUrl) {
              if (!loadedFiles[item.type]) {
                loadedFiles[item.type] = [];
              }
              loadedFiles[item.type].push({
                name: item.name,
                url: item._fileUrl,
              });
            }
          });
          setUploadFiles((prev) => ({ ...prev, ...loadedFiles }));
        }
      });

      // Fetch CRA attachment config
      getCraAttachmentConfig(Number(orderId))
        .then((res) => {
          if (res && Array.isArray(res)) {
            setCraAttachmentConfig(res);

            // 如果接口返回有值，自动勾选 exceedsHighPremiumThreshold 为 "A"
            if (res.length > 0) {
              form.setFieldsValue({
                exceedsHighPremiumThreshold: "A",
              });
            }
          }
        })
        .catch((err) => {
          console.error("Failed to fetch CRA attachment config:", err);
        });
    }
  }, [orderId, form, isZuuMode]);

  useEffect(() => {
    if (fromOwnAccount === "B") return;

    if (form.getFieldValue("thirdPartyPayerRelation") || form.getFieldValue("thirdPartyPayerInfo")) {
      form.setFieldsValue({
        thirdPartyPayerRelation: undefined,
        thirdPartyPayerInfo: undefined,
      });
    }
  }, [fromOwnAccount, form]);

  // CRA - 分銷/交付風險（分銷管道）
  // 規則：
  // - 客戶是否由第三方介紹 = A（是） => 客戶的投保指示是否通過第三方傳達？固定為 B（否），且不可修改
  // - 客戶是否由第三方介紹 = B（否）或 C（N/A） => 固定為 C（N/A），且不可修改
  useEffect(() => {
    if (!referredByThirdParty) return;

    if (referredByThirdParty === "A") {
      if (instructionThroughThirdParty !== "B" || form.getFieldValue("thirdPartyInstructionProof")) {
        form.setFieldsValue({
          instructionThroughThirdParty: "B",
          thirdPartyInstructionProof: undefined,
        });
      }
      return;
    }

    if (referredByThirdParty === "B" || referredByThirdParty === "C") {
      if (instructionThroughThirdParty !== "C" || form.getFieldValue("thirdPartyInstructionProof")) {
        form.setFieldsValue({
          instructionThroughThirdParty: "C",
          thirdPartyInstructionProof: undefined,
        });
      }
    }
  }, [referredByThirdParty, instructionThroughThirdParty, form]);

  const onFinish = async (values: RiskAssessmentVO) => {
    if (!orderId) return;

    // Ensure fullUboAndDirectorList defaults to "C" if not set
    if (!values.fullUboAndDirectorList) {
      values.fullUboAndDirectorList = "C";
    }

    setLoading(true);
    try {
      const newAnswer = buildRiskAssessmentAnswers(values, originalCraData?.answer, craVariant);

      console.log(newAnswer);

      const payload: OrderCraDTO = {
        orderId: Number(orderId),
        answer: newAnswer,
        highPremiumOrders: highPremiumOrders.filter((item) => !item.isInternal),
        signatureImage: signatureImage, // 包含业务代表签名
      };

      await saveCustomerRiskAssessment(payload);
      message.success("儲存成功");

      // 提交审核成功后返回列表页
      setTimeout(() => {
        // Check URL params for source information
        const searchParams = new URLSearchParams(location.search);
        const from = searchParams.get("from");
        const customerId = searchParams.get("customerId");

        if (from === "customer-detail") {
          // Return to customer detail page when the source is customer detail.
          navigate(customerId ? `/customers/${customerId}` : "/orders");
        } else {
          // Return to order list
          navigate("/orders");
        }
      }, 500); // 延迟500ms，让用户看到成功提示
    } catch (err) {
      console.error("Failed to save risk evaluation:", err);
      message.error("儲存失敗");
    } finally {
      setLoading(false);
    }
  };

  // 处理提交审核按钮点击 - 打开签名弹窗
  const handleSubmitClick = async () => {
    try {
      // 验证表单
      await form.validateFields();

      // 表单验证通过，打开签名弹窗
      setTempSignature(signatureImage);
      setIsSignatureReady(!!signatureImage);
      setIsSignatureModalOpen(true);
    } catch (error) {
      console.log("表单验证失败:", error);
      onFinishFailed(error);
    }
  };

  // 处理确认签名按钮点击 - 检查签名是否存在
  const handleConfirmSignature = () => {
    // 检查是否有签名内容
    if (!signaturePadRef.current?.hasContent()) {
      message.error("請先簽名後再提交審核");
      return;
    }

    // 调用 SignaturePad 的 save 方法，会触发 handleSaveSignature
    signaturePadRef.current?.save();
  };

  // 保存签名（SignaturePad 的 onSave 回调）
  const handleSaveSignature = async (dataUrl: string) => {
    // 保存签名到临时状态
    setTempSignature(dataUrl);
    // 保存签名到主状态
    setSignatureImage(dataUrl);
    // 关闭弹窗
    setIsSignatureModalOpen(false);
    setIsSignatureReady(false);

    // 直接调用提交逻辑
    if (!orderId) return;

    setLoading(true);
    try {
      // 获取表单值
      const values = form.getFieldsValue();

      const newAnswer = buildRiskAssessmentAnswers(values, originalCraData?.answer, craVariant);

      const payload: OrderCraDTO = {
        orderId: Number(orderId),
        answer: newAnswer,
        highPremiumOrders: highPremiumOrders.filter((item) => !item.isInternal),
        signatureImage: dataUrl, // 使用新签名
      };

      await saveCustomerRiskAssessment(payload);
      message.success("儲存成功");

      // 提交审核成功后返回列表页
      setTimeout(() => {
        const searchParams = new URLSearchParams(location.search);
        const from = searchParams.get("from");
        const customerId = searchParams.get("customerId");

        if (from === "customer-detail") {
          navigate(customerId ? `/customers/${customerId}` : "/orders");
        } else {
          navigate("/orders");
        }
      }, 500);
    } catch (err) {
      console.error("Failed to save risk evaluation:", err);
      message.error("儲存失敗");
    } finally {
      setLoading(false);
    }
  };

  // 处理表单验证失败
  const onFinishFailed = (errorInfo: any) => {
    console.log("表单验证失败:", errorInfo);

    // 获取所有错误字段
    const errorFields = errorInfo.errorFields || [];

    if (errorFields.length === 0) return;

    // 只获取第一个错误字段
    const firstErrorField = errorFields[0];
    const fieldName = firstErrorField.name[0];

    // 获取字段的 label（通过查找表单项）
    let fieldLabel = fieldName;

    // 尝试从表单中获取字段 label
    const formItem = document.querySelector(`[id="${fieldName}"]`)?.closest(".ant-form-item");
    if (formItem) {
      const labelElement = formItem.querySelector(".ant-form-item-label label");
      if (labelElement) {
        fieldLabel = labelElement.textContent || fieldName;
      }
    }

    // 使用 message.error 显示错误提示
    message.error(`請填寫必填項：${fieldLabel}`);

    // 滚动到第一个错误字段
    form.scrollToField(fieldName, {
      behavior: "smooth",
      block: "center",
      scrollMode: "if-needed",
    });
  };

  const add = () => {
    setEditingOrder(undefined);
    setIsOrderModalOpen(true);
  };

  const edit = (record: HighPremiumOrder & { key: React.Key }) => {
    console.log("点击编辑的保单数据:", record);
    console.log("是否为内部保单:", record.isInternal);
    setEditingOrder(record);
    setIsOrderModalOpen(true);
  };

  const handleSaveOrder = async (values: HighPremiumOrder) => {
    if (!orderId) {
      message.error("訂單ID不存在");
      return;
    }
    console.log("表单提交的值:", values);
    console.log("当前编辑的保单:", editingOrder);
    try {
      const newData = [...highPremiumOrders];

      if (editingOrder) {
        // 編輯現有訂單 - 使用更精确的匹配条件,包括 isInternal 字段
        const index = newData.findIndex((item: any) => {
          // 优先使用 key 或 id 匹配
          const keyMatch = (item.key && item.key === editingOrder.key) || (item.id && item.id === editingOrder.id);
          // 同时检查 orderNo 和 isInternal,确保匹配到正确的保单
          const orderMatch = item.orderNo === editingOrder.orderNo && item.isInternal === editingOrder.isInternal;
          return keyMatch || orderMatch;
        });

        console.log("找到的匹配索引:", index);

        if (index > -1) {
          console.log("原数据:", newData[index]);
          newData[index] = {
            ...newData[index],
            ...values,
          };
          console.log("更新后数据:", newData[index]);
        } else {
          console.error("未找到匹配的保单!");
        }
      } else {
        // 新增訂單
        newData.push({
          ...values,
        } as any);
      }
      console.log("保存前的完整数据 newData:", newData);

      const dataToSave = newData.filter((item) => !item.isInternal);
      console.log("过滤后要发送给后端的数据:", dataToSave);

      // 調用 API 保存
      await saveCraInsuredPolicy({
        orderId: Number(orderId),
        highPremiumOrders: dataToSave,
      });

      // 刷新風險評估數據以獲取最新的保單列表
      const craData = await getRiskEvaluation(Number(orderId));
      console.log("保存后从后端获取的数据:", craData?.insuredPolicy?.highPremiumOrders);

      if (craData && craData.insuredPolicy) {
        setOriginalCraData(craData);
        setHighPremiumOrders(craData.insuredPolicy.highPremiumOrders || []);
      }

      message.success("儲存成功");
      setIsOrderModalOpen(false);
      setEditingOrder(undefined);

      // 刷新附件配置
      const config = await getCraAttachmentConfig(Number(orderId));
      if (config && Array.isArray(config)) {
        setCraAttachmentConfig(config);
      }
    } catch (err) {
      console.error("保存失敗:", err);
      throw err;
    }
  };
  const handleBack = () => {
    // Check URL params for source information
    const searchParams = new URLSearchParams(location.search);
    const from = searchParams.get("from");
    const customerId = searchParams.get("customerId");

    if (from === "customer-detail") {
      // Return to customer detail page when the source is customer detail.
      navigate(customerId ? `/customers/${customerId}` : "/orders");
    } else {
      // Return to order list
      navigate("/orders");
    }
  };

  const handleDelete = async (key: React.Key) => {
    if (!orderId) {
      message.error("訂單ID不存在");
      return;
    }

    try {
      const newData = highPremiumOrders.filter((item: any) => item.orderNo !== key);

      // 調用 API 保存更新後的列表
      await saveCraInsuredPolicy({
        orderId: Number(orderId),
        highPremiumOrders: newData.filter((item) => !item.isInternal),
      });

      // 刷新風險評估數據以獲取最新的保單列表
      const craData = await getRiskEvaluation(Number(orderId));
      if (craData && craData.insuredPolicy) {
        setOriginalCraData(craData);
        setHighPremiumOrders(craData.insuredPolicy.highPremiumOrders || []);
      }

      message.success("刪除成功");

      // 刷新附件配置
      const config = await getCraAttachmentConfig(Number(orderId));
      if (config && Array.isArray(config)) {
        setCraAttachmentConfig(config);
      }
    } catch (err) {
      console.error("刪除失敗:", err);
      // 錯誤時不需要恢復，因為本地狀態未變
    }
  };

  const [uploadFiles, setUploadFiles] = useState<Record<string, UploadFile[]>>({});

  // Preview Modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<{ url: string; name: string }>();

  const handlePreview = async (file: UploadFile) => {
    // 优先使用 ossPath，如果没有则使用 path
    const filePath = (file as any).ossPath || (file as any).path || file.response || file.url;

    if (!filePath) {
      message.error("檔案路徑不可用");
      return;
    }

    // 对于本地新上传的文件（有 originFileObj），使用 FileReader 读取
    if (file.originFileObj && !file.url) {
      const previewUrl = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj as Blob);
        reader.onload = () => resolve(reader.result as string);
      });
      setPreviewFile({ url: previewUrl, name: file.name || "unnamed" });
      setPreviewOpen(true);
      return;
    }

    // 其他情况：传递 ossPath/path 给 FilePreviewModal，让它通过 API 获取
    setPreviewFile({ url: filePath, name: file.name || "unnamed" });
    setPreviewOpen(true);
  };

  // ... (previous useEffect code)

  const handleUpload = async (options: any, fileType: FileType) => {
    const { onSuccess, onError, file } = options;
    if (!orderId) {
      message.error("訂單ID不存在");
      onError(new Error("No Order ID"));
      return;
    }

    // 使用 Hook 处理图片压缩
    const fileToUpload = await handleBeforeUpload(file);
    if (fileToUpload === false) {
      // 用户取消上传
      onError(new Error("User cancelled"));
      return;
    }

    try {
      // 1. Upload File to Object Storage
      const formData = new FormData();
      formData.append("file", fileToUpload);
      formData.append("module", "ORDER");
      // Use orderNo if available, otherwise use orderId as fallback
      formData.append("key", orderData?.orderNo || String(orderId));

      const filePath = await uploadFile(formData);

      // 2. Link Attachment to Order
      const uploadData: OrderAttachmentDTO = {
        orderId: Number(orderId),
        token: filePath,
        type: fileType,
      };

      await uploadAttachment(uploadData);

      message.success(`上載成功`);

      // Refresh attachments to get IDs for correct deletion
      const files = await getOrderAttachments(Number(orderId), fileType as any);
      const mappedFiles = files.map((f: any) => ({
        uid: f.id ? String(f.id) : f.attachmentId ? String(f.attachmentId) : String(Math.random()),
        name: f.name || f.fileName || "Unknown File",
        status: "done" as const,
        url: f.url || f.path || f.fileUrl,
        path: f.path,
        ossPath: f.ossPath,
        attachmentId: f.attachmentId,
      }));

      setUploadFiles((prev) => ({
        ...prev,
        [fileType]: mappedFiles,
      }));

      onSuccess(filePath, fileToUpload);
    } catch (err) {
      console.error(err);
      message.error(`${file.name} 上載失敗`);
      onError(err);
    }
  };

  const handleRemove = async (file: UploadFile, fileType: FileType) => {
    const attachmentId = (file as any).attachmentId;

    if (attachmentId && orderId) {
      try {
        await deleteAttachment(Number(orderId), Number(attachmentId));
        message.success("刪除成功");
      } catch (err) {
        console.error("Failed to delete attachment:", err);
        message.error("刪除失敗");
        return false;
      }
    }

    setUploadFiles((prev) => ({
      ...prev,
      [fileType]: (prev[fileType] || []).filter((f) => f.uid !== file.uid),
    }));
    return true;
  };

  const renderUploadBox = (fileType: FileType, label: string, isRequired: boolean = false) => {
    let displayFiles = uploadFiles[fileType] || [];
    // Append insuredPolicy files to AssetIncomeProof
    if (fileType === FileType.CraAssetIncomeProof) {
      const insuredPolicyFiles = uploadFiles["INSURED_POLICY"] || [];
      displayFiles = [...displayFiles, ...insuredPolicyFiles];
    }

    return (
      <div className="upload-item-container">
        <Upload
          listType="picture-card"
          fileList={displayFiles}
          customRequest={(options) => handleUpload(options, fileType)}
          onRemove={(file) => handleRemove(file, fileType)}
          onPreview={handlePreview}
          disabled={isReadOnly}
          showUploadList={{ showPreviewIcon: true, showRemoveIcon: !isReadOnly }}
          itemRender={(originNode, file, fileList, actions) => {
            const fileName = file.name?.toLowerCase() || "";
            let iconSrc = null;
            let isImage = false;

            if (file.status !== "uploading") {
              if (fileName.endsWith(".pdf")) {
                iconSrc = pdfIcon;
              } else if (/\.(xls|xlsx|csv)$/.test(fileName)) {
                iconSrc = excelIcon;
              } else if (/\.(doc|docx)$/.test(fileName)) {
                iconSrc = wordIcon;
              } else {
                // Assume image if not document types above
                isImage = true;
              }
            }

            return (
              <div
                className="ant-upload-list-picture-card-container"
                style={{
                  position: "relative",
                  display: "inline-block",
                  marginRight: 8,
                  marginBottom: 8,
                  width: 160,
                  height: 110,
                }}
              >
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    padding: 8,
                    border: "1px solid #d9d9d9",
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    overflow: "hidden",
                    position: "relative",
                  }}
                  onClick={() => handlePreview(file)}
                >
                  {iconSrc ? (
                    <img src={iconSrc} alt={file.name} style={{ width: 60, height: 60, objectFit: "contain" }} />
                  ) : isImage ? (
                    <img
                      src={file.thumbUrl || file.url || (file.originFileObj ? URL.createObjectURL(file.originFileObj) : "")}
                      alt={file.name}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    // Fallback or uploading state
                    originNode
                  )}
                </div>
                {!isReadOnly && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      actions.remove();
                    }}
                    style={{
                      position: "absolute",
                      top: -10,
                      right: -10,
                      background: "#ff4d4f",
                      color: "white",
                      borderRadius: "50%",
                      width: 24,
                      height: 24,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      zIndex: 10,
                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    }}
                  >
                    <CloseOutlined style={{ fontSize: 14 }} />
                  </div>
                )}
              </div>
            );
          }}
        >
          {displayFiles.length < 1 && !isReadOnly && (
            <div>
              <PlusOutlined style={{ fontSize: 24, color: "#999" }} />
              <div style={{ marginTop: 8, color: "#999" }}>上載</div>
            </div>
          )}
        </Upload>
        <div className="upload-label" style={{ marginTop: 8, color: "#666", fontSize: 14 }}>
          {isRequired && <span className="required-mark">*</span>}
          {label}
        </div>
      </div>
    );
  };

  // Assign keys to highPremiumOrders if missing
  const productData = highPremiumOrders.map((item: any, index: number) => ({
    ...item,
    key: item.key || item.id || `temp-${index}`,
  }));

  const annualTotalPremium = useMemo(
    () =>
      highPremiumOrders.reduce(
        (total, item) => total + Number(item?.annualPremium || 0),
        0,
      ),
    [highPremiumOrders],
  );

  const totalOneTimePayment = useMemo(
    () =>
      highPremiumOrders.reduce(
        (total, item) => total + Number(item?.oneTimePremium || 0),
        0,
      ),
    [highPremiumOrders],
  );

  // CRA 風險結果映射（後端枚舉 -> 前端中文顯示）
  const mapCraRiskResult = (result?: CraRiskResult): "低风险" | "中风险" | "高风险" | "" => {
    switch (result) {
      case CraRiskResult.NoRisk:
      case CraRiskResult.LowRisk:
        return "低风险";
      case CraRiskResult.MediumRisk:
        return "中风险";
      case CraRiskResult.HighRisk:
        return "高风险";
      default:
        return "";
    }
  };

  const handleViewRiskResults = async () => {
    if (!orderId) {
      message.error("訂單ID不存在");
      return;
    }

    // 先进行表单验证
    try {
      await form.validateFields();
    } catch (errorInfo: any) {
      console.log("表单验证失败:", errorInfo);

      // 获取所有错误字段
      const errorFields = errorInfo.errorFields || [];

      if (errorFields.length > 0) {
        // 只获取第一个错误字段
        const firstErrorField = errorFields[0];
        const fieldName = firstErrorField.name[0];

        // 获取字段的 label（通过查找表单项）
        let fieldLabel = fieldName;

        // 尝试从表单中获取字段 label
        const formItem = document.querySelector(`[id="${fieldName}"]`)?.closest(".ant-form-item");
        if (formItem) {
          const labelElement = formItem.querySelector(".ant-form-item-label label");
          if (labelElement) {
            fieldLabel = labelElement.textContent || fieldName;
          }
        }

        // 使用 message.error 显示错误提示
        message.error(`請填寫必填項：${fieldLabel}`);

        // 滚动到第一个错误字段
        form.scrollToField(fieldName, {
          behavior: "smooth",
          block: "center",
          scrollMode: "if-needed",
        });
      }
      return;
    }

    setLoadingRiskResults(true);
    setIsRiskModalOpen(true);

    try {
      // 获取当前表单值
      const values = form.getFieldsValue();

      // 确保 fullUboAndDirectorList 有默认值
      if (!values.fullUboAndDirectorList) {
        values.fullUboAndDirectorList = "C";
      }

      const answer = buildRiskAssessmentAnswers(values, originalCraData?.answer, craVariant);

      // 调用新的 POST 接口
      const craData = await getCraResultByAnswer(answer);
      if (craData && craData.length > 0) {
        setCraResults(craData);
      } else {
        setCraResults([]);
      }
    } catch (error) {
      console.error("Failed to fetch risk results:", error);
      message.error("獲取風險評估結果失敗");
    } finally {
      setLoadingRiskResults(false);
    }
  };
  const closeSignatureModal = () => {
    setIsSignatureModalOpen(false);
    setIsSignatureReady(false);
  };

  const clearSignature = () => {
    signaturePadRef.current?.clear();
  };

  const closeRiskModal = () => {
    setIsRiskModalOpen(false);
  };

  const cancelOrderModal = () => {
    setIsOrderModalOpen(false);
    setEditingOrder(undefined);
  };

  const closePreview = () => {
    setPreviewOpen(false);
  };

  const questionSectionProps = {
    form,
    isReadOnly,
    renderUploadBox,
    craAttachmentConfig,
    productData,
    companyList,
    annualTotalPremium,
    totalOneTimePayment,
    add,
    edit,
    handleDelete,
  };


  return {
    hideHeader,
    isReadOnly,
    isZuuMode,
    questionSectionProps,
    renderUploadBox,
    craAttachmentConfig,
    originalCraData,
    orderData,
    navigate,
    loading,
    handleBack,
    handleViewRiskResults,
    handleSubmitClick,
    form,
    onFinish,
    onFinishFailed,
    isSignatureModalOpen,
    closeSignatureModal,
    signaturePadRef,
    handleSaveSignature,
    tempSignature,
    currentUser,
    clearSignature,
    handleConfirmSignature,
    isSignatureReady,
    setIsSignatureReady,
    isRiskModalOpen,
    closeRiskModal,
    loadingRiskResults,
    craResults,
    mapCraRiskResult,
    CRA_PROJECT_LABELS,
    isOrderModalOpen,
    cancelOrderModal,
    handleSaveOrder,
    editingOrder,
    companyList,
    previewOpen,
    closePreview,
    previewFile,
  };
}


export type RiskAssessmentController = ReturnType<typeof useRiskAssessmentController>;
